/**
 * ParticipantContext — shared state for the authenticated app shell.
 *
 * Rationale: several widgets (sidebar badges, alerts summary, dashboard
 * balance cards, news relevance decorator) previously each fired their own
 * API call on mount. This context consolidates the small, shared slice of
 * participant data so widgets read from one place. Heavy page-specific data
 * (portfolio holdings, fund-allocation editor state) still lives on the page.
 *
 * What's shared here:
 *   • profile              — GET /api/tsp/participant/profile   (name, agency, retirementSystem, etc.)
 *   • balance              — GET /api/tsp/participant/balance   (total/traditional/roth)
 *   • iftStatus            — GET /api/tsp/participant/ift-status (used, remaining)
 *   • alerts               — GET /api/tsp/participant/alerts     (dashboard alerts)
 *   • pendingActions       — GET /api/participant/pending-actions (documents + loan packages)
 *   • funds                — GET /api/participant/funds          (fund allocation map)
 *
 * Refresh semantics: initial load on mount, manual `reload()` exposed, and
 * an auto-refresh every 5 minutes for the three fast-changing fields
 * (alerts, pendingActions, iftStatus). No polling on profile/balance/funds.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const ParticipantContext = createContext(null);

const AUTO_REFRESH_MS = 5 * 60 * 1000;

export const ParticipantProvider = ({ children }) => {
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);

  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [iftStatus, setIftStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [funds, setFunds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const intervalRef = useRef(null);

  const loadStatic = useCallback(async () => {
    // Profile, balance, fund allocation — slow-changing, fetch once
    const [p, b, f] = await Promise.allSettled([
      api.get('/tsp/participant/profile'),
      api.get('/tsp/participant/balance'),
      api.get('/participant/funds'),
    ]);
    if (p.status === 'fulfilled') setProfile(p.value.data);
    if (b.status === 'fulfilled') setBalance(b.value.data);
    if (f.status === 'fulfilled') setFunds(f.value.data?.funds || []);
  }, []);

  const loadDynamic = useCallback(async () => {
    // IFT status, alerts, pending actions — refresh periodically
    const [i, a, pa] = await Promise.allSettled([
      api.get('/tsp/participant/ift-status'),
      api.get('/tsp/participant/alerts'),
      api.get('/participant/pending-actions'),
    ]);
    if (i.status === 'fulfilled') setIftStatus(i.value.data);
    if (a.status === 'fulfilled') setAlerts(Array.isArray(a.value.data) ? a.value.data : []);
    if (pa.status === 'fulfilled') {
      const list = Array.isArray(pa.value.data) ? pa.value.data : (pa.value.data?.pending || []);
      setPendingActions(list);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadStatic(), loadDynamic()]);
    } finally {
      setLoading(false);
      setInitialised(true);
    }
  }, [loadStatic, loadDynamic]);

  // Initial + when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null); setBalance(null); setIftStatus(null);
      setAlerts([]); setPendingActions([]); setFunds([]);
      setInitialised(false);
      return;
    }
    reload();
  }, [isAuthenticated, reload]);

  // Auto-refresh dynamic slice
  useEffect(() => {
    if (!isAuthenticated) return;
    intervalRef.current = setInterval(() => { loadDynamic(); }, AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, loadDynamic]);

  const value = {
    profile, balance, iftStatus, alerts, pendingActions, funds,
    loading, initialised,
    // Derived counts for badges
    alertsCount: (alerts || []).length,
    pendingActionsCount: (pendingActions || []).filter(a => !a.submitted).length || (pendingActions || []).length,
    iftUsed: iftStatus?.used ?? 0,
    iftRemaining: iftStatus?.remaining ?? 2,
    reload,
    refreshDynamic: loadDynamic,
  };

  return (
    <ParticipantContext.Provider value={value}>
      {children}
    </ParticipantContext.Provider>
  );
};

export const useParticipant = () => {
  const ctx = useContext(ParticipantContext);
  if (!ctx) {
    throw new Error('useParticipant must be used within ParticipantProvider');
  }
  return ctx;
};
